import { format } from 'date-fns';
import {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';

import Page from '@/components/Page';
import ROUTES from '@/routes/routes';
import { OpsRotaApiError } from '@/services/opsRota/client';
import type { OpsSchedule } from '@/services/opsRota/types';
import {
  trackOpsLeavePrompted,
  trackOpsScheduleChanged,
  trackOpsScheduleFailed,
  trackOpsToolOpened,
} from '@/util/vexo';
import type { OpsLeaveTrigger } from '@/util/vexo';

import { failReason } from './analytics';

import DayBlocks from './components/DayBlocks';
import MonthCalendar from './components/MonthCalendar';
import OpsDialog from './components/OpsDialog';
import OpsPreviewCard from './components/OpsPreviewCard';
import { OpsToastProvider, useOpsToast } from './components/OpsToast';
import RepeatScheduleModal from './components/RepeatScheduleModal';
import RosterModal from './components/RosterModal';
import ScheduleForm from './components/ScheduleForm';
import { dayLabel, monthOf, panelHeading, zoneLabel } from './dates';
import { useBeforeUnload } from './hooks/useBeforeUnload';
import { onLeaveGuardEntry, useLeaveGuard } from './hooks/useLeaveGuard';
import { useOpsSchedules, useScheduleMutations } from './hooks/useOpsRota';
import {
  DELETE_TITLE,
  DELETED_TOAST,
  deleteConfirmText,
  DISCARD_MESSAGE,
  DISCARD_TITLE,
  LOAD_ERROR,
  LOADING,
  REFRESH_ERROR,
  STALE_MESSAGE,
} from './messages';
import { gridRange, pickRange, Selection } from './monthGrid';
import {
  Body,
  Card,
  CardHeader,
  CardTitle,
  DangerButton,
  DialogContent,
  DialogFooter,
  DialogMessage,
  ErrorBox,
  HeaderAside,
  LinkButton,
  Muted,
  Panel,
  PageTitle,
  PanelHeading,
  Root,
  TextButton,
  ZoneNote,
} from './Operations.styles';

type Mode =
  | { kind: 'view' }
  | { kind: 'add' }
  | { kind: 'edit'; schedule: OpsSchedule };

const VIEW: Mode = { kind: 'view' };

// GRAVY-10878 Admin › Operations: who is on ops duty, by date. Tenant admins
// only (route and API). Every date here is an ops-zone date from graphy.
const OperationsPage = () => {
  const showToast = useOpsToast();
  const navigate = useNavigate();

  const [month, setMonth] = useState(() => format(new Date(), 'yyyy-MM'));
  const [selection, setSelection] = useState<Selection | null>(null);
  const [mode, setMode] = useState<Mode>(VIEW);
  // Bumped on every save / cancel / mode change so the form starts fresh.
  const [formKey, setFormKey] = useState(0);
  const [dirty, setDirty] = useState(false);
  const [pending, setPending] = useState<(() => void) | null>(null);
  // What raised the unsaved-changes prompt, for its analytics event.
  const leaveTrigger = useRef<OpsLeaveTrigger>('in_page');
  // A save in flight locks the page. Discard cannot stop the request,
  // so nothing offers it until the save settles.
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);
  const onBusyChange = useCallback((busy: boolean) => {
    savingRef.current = busy;
    setSaving(busy);
  }, []);
  const ask = useCallback((trigger: OpsLeaveTrigger, action: () => void) => {
    if (savingRef.current) return;
    leaveTrigger.current = trigger;
    setPending(() => action);
  }, []);
  const keepEditing = () => {
    trackOpsLeavePrompted({ trigger: leaveTrigger.current, choice: 'keep' });
    setPending(null);
  };
  const [deleting, setDeleting] = useState<OpsSchedule | null>(null);
  // The Repeat modal (§11) and the roster modal.
  const [repeatOf, setRepeatOf] = useState<OpsSchedule | null>(null);
  const repeatTrigger = useRef<HTMLElement | null>(null);
  const [rosterOpen, setRosterOpen] = useState(false);
  const rosterLink = useRef<HTMLButtonElement>(null);
  // Where focus goes when the roster closes: the control that opened it
  // (the header link, or the empty dropdown's link's manager field).
  const rosterReturn = useRef<HTMLElement | null>(null);
  const openRoster = (from: HTMLElement | null) => {
    trackOpsToolOpened({ tool: 'roster' });
    rosterReturn.current = from;
    setRosterOpen(true);
  };

  const monthQuery = useOpsSchedules(gridRange(month), { keepPrevious: true });
  const isRange = selection !== null && selection.end !== null;
  const day = selection && !isRange ? selection.start : null;
  // With the day before's overnight blocks that run into the day.
  const dayQuery = useOpsSchedules(day ? { from: day, to: day } : null, {
    carryOver: true,
  });

  // `today` must survive a failed month fetch (a month change that errors
  // drops the placeholder data), or the today ring and the past-day read-only
  // guard would vanish with it.
  const lastToday = useRef<string | null>(null);
  const lastZone = useRef<string | null>(null);
  const today =
    monthQuery.data?.meta.today ??
    dayQuery.data?.meta.today ??
    lastToday.current;
  const timeZone =
    monthQuery.data?.meta.time_zone ??
    dayQuery.data?.meta.time_zone ??
    lastZone.current;
  lastToday.current = today;
  lastZone.current = timeZone;
  const { remove } = useScheduleMutations();

  // Open on the ops zone's today (from graphy), not the browser's.
  useEffect(() => {
    if (today && selection === null) {
      setSelection({ start: today, end: null });
      setMonth(monthOf(today));
    }
  }, [today, selection]);

  useBeforeUnload(dirty);
  useLeaveGuard(
    dirty,
    useCallback(
      // Replace the guard's extra history entry, so Back from `href` lands
      // on Operations once, not twice.
      (href: string) =>
        ask('link', () => navigate(href, { replace: onLeaveGuardEntry() })),
      [ask, navigate],
    ),
    // The browser's Back asks first too; Discard then goes back.
    useCallback((proceed: () => void) => ask('back', proceed), [ask]),
    // Opened first in the tab, so Back has nowhere to go: go home,
    // in place of the guard's extra entry.
    useCallback(() => navigate(ROUTES.home, { replace: true }), [navigate]),
  );

  const monthSchedules = useMemo(
    () => monthQuery.data?.schedules ?? [],
    [monthQuery.data],
  );
  const marked = useMemo(
    () => new Set(monthSchedules.map((s) => s.date)),
    [monthSchedules],
  );
  // A block from today on that still holds a deactivated manager.
  const flagged = useMemo(
    () =>
      new Set(
        monthSchedules
          .filter(
            (s) =>
              today !== null &&
              s.date >= today &&
              s.managers.some((m) => !m.active),
          )
          .map((s) => s.date),
      ),
    [monthSchedules, today],
  );

  const blocks = dayQuery.data?.schedules ?? [];
  // A failed background refresh keeps the cached day (React Query
  // keeps `data` but drops `isSuccess`), so render on data, not on success.
  const dayLoaded = dayQuery.data !== undefined;
  // OP-14: nothing is added on a past day (no form, no Add another). A
  // range never starts in the past. Edit and Delete follow each block's
  // `editable` instead (BR-07), so a past day's block still running is
  // editable.
  const readOnly = Boolean(
    selection && today && selection.start < today && !isRange,
  );
  // UI spec §8.1, plus no add form on a past day. Editing an editable
  // block opens its form whatever the day.
  const showForm =
    selection !== null &&
    (mode.kind === 'edit' ||
      (!readOnly &&
        (isRange ||
          mode.kind === 'add' ||
          (dayLoaded && blocks.length === 0))));
  const canCancel = !isRange && mode.kind !== 'view' && blocks.length > 0;

  // Unsaved changes + another action → confirm first.
  const guard = (action: () => void) =>
    dirty ? ask('in_page', action) : action();

  // The add form on an empty day shows in view mode. Typing pins it as an
  // add, so a refetch that brings in another admin's block cannot swap it
  // for the block list and drop the input. No remount: formKey stays. (A
  // range shows the form in any mode, so pinning it there changes nothing.)
  const onDirtyChange = useCallback((next: boolean) => {
    setDirty(next);
    if (next) setMode((m) => (m.kind === 'view' ? { kind: 'add' } : m));
  }, []);

  // Where focus goes once the delete dialog closes: back to the kebab that
  // opened it (Cancel, or a failed delete that left the block), else the
  // panel heading. The deleted block's kebab is gone, and focus must not
  // fall to <body>. The same goes for every panel swap
  // (Edit, Cancel, Add another, a save) that unmounts the focused control.
  const deleteTrigger = useRef<HTMLElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const [refocus, setRefocus] = useState<'trigger' | 'heading' | null>(null);

  const resetForm = (next: Mode) => {
    setDirty(false);
    setMode(next);
    setFormKey((k) => k + 1);
  };

  const selectDate = (date: string) =>
    guard(() => {
      setSelection((current) =>
        pickRange(current, date, today, (d) => marked.has(d)),
      );
      resetForm(VIEW);
    });

  const onSaved = (
    title: string,
    body: string | undefined,
    addAnother: boolean,
  ) => {
    showToast(title, body);
    resetForm(addAnother ? { kind: 'add' } : VIEW);
    setRefocus('heading');
  };

  const leaveForm = () => {
    resetForm(VIEW);
    setRefocus('heading');
  };

  useEffect(() => {
    if (!refocus) return;
    const trigger = deleteTrigger.current;
    if (refocus === 'trigger' && trigger?.isConnected) trigger.focus();
    else headingRef.current?.focus();
    deleteTrigger.current = null;
    setRefocus(null);
  }, [refocus]);

  const openDelete = (s: OpsSchedule, trigger: HTMLElement | null) => {
    trackOpsToolOpened({ tool: 'delete' });
    deleteTrigger.current = trigger;
    setDeleting(s);
  };

  const cancelDelete = () => {
    if (remove.isPending) return;
    setDeleting(null);
    setRefocus('trigger');
  };

  // The dialog stays open (Delete disabled) until the request settles, so a
  // double click cannot send a second delete.
  const confirmDelete = async () => {
    if (!deleting || remove.isPending) return;
    const target = deleting;
    try {
      await remove.mutateAsync({
        id: target.id,
        lockVersion: target.lock_version,
      });
      trackOpsScheduleChanged({
        action: 'delete',
        days_count: 1,
        managers_count: target.managers.length,
      });
      showToast(DELETED_TOAST.title, DELETED_TOAST.body);
      setRefocus('heading');
    } catch (err) {
      trackOpsScheduleFailed({ action: 'delete', ...failReason(err, true) });
      setRefocus('trigger');
      // 409: changed meanwhile; 404: already gone. The mutation's
      // invalidation reloads the day either way.
      if (
        err instanceof OpsRotaApiError &&
        (err.isConflict || err.status === 404)
      ) {
        showToast(STALE_MESSAGE);
      } else {
        showToast(
          err instanceof OpsRotaApiError
            ? err.message
            : 'Could not delete the schedule. Try again.',
        );
      }
    } finally {
      setDeleting(null);
    }
  };

  const retry = () => {
    monthQuery.refetch();
    if (day) dayQuery.refetch();
  };

  // The first load failed, so there is no today and nothing selected.
  // The panel says so itself rather than staying an empty box.
  const firstLoadFailed =
    selection === null && (monthQuery.isError || dayQuery.isError);
  const monthFailed = monthQuery.isError && !monthQuery.data;
  const dayFailed = dayQuery.isError && !dayLoaded;
  const loadError = (monthFailed || dayFailed) && (
    <ErrorBox role="alert" data-testid="opsLoadError">
      {LOAD_ERROR}
      <LinkButton type="button" data-testid="opsRetryButton" onClick={retry}>
        Retry
      </LinkButton>
    </ErrorBox>
  );
  // Only a refresh failed, so the page still shows what it had.
  const refreshError = !loadError &&
    (monthQuery.isError || dayQuery.isError) && (
      <ErrorBox role="status" data-testid="opsRefreshError">
        {REFRESH_ERROR}
        <LinkButton type="button" onClick={retry}>
          Retry
        </LinkButton>
      </ErrorBox>
    );

  let panelBody: ReactNode = null;
  if (firstLoadFailed) {
    panelBody = loadError;
  } else if (selection && showForm) {
    panelBody = (
      <ScheduleForm
        key={formKey}
        date={selection.start}
        endDate={selection.end}
        schedule={mode.kind === 'edit' ? mode.schedule : undefined}
        onSaved={onSaved}
        onCancel={leaveForm}
        onDirtyChange={onDirtyChange}
        canCancel={canCancel}
        onManageManagers={openRoster}
        onBusyChange={onBusyChange}
      />
    );
  } else if (selection && dayLoaded) {
    panelBody = (
      <DayBlocks
        blocks={blocks}
        readOnly={readOnly}
        onEdit={(s) => {
          trackOpsToolOpened({ tool: 'edit' });
          resetForm({ kind: 'edit', schedule: s });
          setRefocus('heading');
        }}
        onRepeat={(s, trigger) => {
          trackOpsToolOpened({ tool: 'repeat' });
          repeatTrigger.current = trigger;
          setRepeatOf(s);
        }}
        onDelete={openDelete}
        onAddAnother={() => {
          resetForm({ kind: 'add' });
          setRefocus('heading');
        }}
      />
    );
  } else if (!monthFailed && !dayFailed) {
    panelBody = <Muted data-testid="opsPanelLoading">{LOADING}</Muted>;
  }

  // Editing a block carried over from the day before edits it on its own
  // date, so the heading names that date.
  const heading =
    selection &&
    (mode.kind === 'edit' && mode.schedule.date !== selection.start
      ? dayLabel(mode.schedule.date)
      : panelHeading(selection.start, selection.end));

  return (
    <Page
      title={<PageTitle>Operations</PageTitle>}
      size="xl"
      contentVarient="true"
    >
      <Root data-testid="operationsPage">
        <OpsPreviewCard
          today={today}
          todayError={monthQuery.isError || dayQuery.isError}
          onRetryToday={retry}
        />

        <Card data-testid="opsScheduleCard" aria-labelledby="opsCardTitle">
          <CardHeader>
            <CardTitle id="opsCardTitle">Add new schedule</CardTitle>
            <HeaderAside>
              {/* The note's room is kept while the zone is unknown, so
                  the header does not shift when it arrives. */}
              {timeZone ? (
                <ZoneNote data-testid="opsZoneNote">
                  Times are in {zoneLabel(timeZone)}
                </ZoneNote>
              ) : (
                <ZoneNote aria-hidden="true" css={{ visibility: 'hidden' }}>
                  Times are in Eastern Time
                </ZoneNote>
              )}
              <LinkButton
                ref={rosterLink}
                type="button"
                data-testid="opsManageManagersLink"
                onClick={() => openRoster(rosterLink.current)}
              >
                Manage managers
              </LinkButton>
            </HeaderAside>
          </CardHeader>

          {!firstLoadFailed && loadError}
          {refreshError}

          <Body>
            <MonthCalendar
              month={month}
              onMonthChange={setMonth}
              today={today}
              selection={selection}
              marked={marked}
              flagged={flagged}
              onSelect={selectDate}
              dimmed={mode.kind === 'edit' || saving}
              testIdPrefix="opsCalendar"
            />
            <Panel data-testid="opsSchedulePanel" data-read-only={readOnly}>
              {selection && (
                <PanelHeading
                  ref={headingRef}
                  tabIndex={-1}
                  data-testid="opsPanelHeading"
                  range={isRange}
                >
                  {heading}
                </PanelHeading>
              )}
              {panelBody}
            </Panel>
          </Body>
        </Card>
      </Root>

      {repeatOf && today && (
        <RepeatScheduleModal
          schedule={repeatOf}
          today={today}
          month={month}
          returnFocus={repeatTrigger.current}
          onClose={() => setRepeatOf(null)}
          onDone={(toast) => {
            showToast(toast.title, toast.body);
            setRepeatOf(null);
          }}
        />
      )}

      {rosterOpen && (
        <RosterModal
          returnFocus={rosterReturn.current}
          onClose={() => setRosterOpen(false)}
        />
      )}

      {deleting && (
        // The ticket's delete copy and a red Delete, in the Operations dialog. Esc, the
        // X and Cancel close it; a finished delete closes it too.
        // While the delete is in flight none of them do.
        <OpsDialog
          title={DELETE_TITLE}
          testId="opsDeleteDialog"
          closeDisabled={remove.isPending}
          width="512px"
          onClose={cancelDelete}
        >
          <DialogContent>
            <DialogMessage>{deleteConfirmText(deleting)}</DialogMessage>
          </DialogContent>
          <DialogFooter>
            <TextButton
              type="button"
              disabled={remove.isPending}
              data-testid="opsDeleteCancelButton"
              onClick={cancelDelete}
            >
              Cancel
            </TextButton>
            <DangerButton
              type="button"
              disabled={remove.isPending}
              data-testid="opsDeleteConfirmButton"
              onClick={confirmDelete}
            >
              Delete
            </DangerButton>
          </DialogFooter>
        </OpsDialog>
      )}

      {pending && (
        // The unsaved-changes confirm, in the Operations dialog like the
        // delete confirm. Keep editing, the X and Esc stay on the form.
        <OpsDialog
          title={DISCARD_TITLE}
          testId="opsDiscardDialog"
          width="512px"
          onClose={keepEditing}
        >
          <DialogContent>
            <DialogMessage>{DISCARD_MESSAGE}</DialogMessage>
          </DialogContent>
          <DialogFooter>
            <TextButton
              type="button"
              data-testid="opsDiscardCancelButton"
              onClick={keepEditing}
            >
              Keep editing
            </TextButton>
            <DangerButton
              type="button"
              data-testid="opsDiscardConfirmButton"
              onClick={() => {
                const go = pending;
                trackOpsLeavePrompted({
                  trigger: leaveTrigger.current,
                  choice: 'discard',
                });
                setPending(null);
                setDirty(false);
                go();
              }}
            >
              Discard
            </DangerButton>
          </DialogFooter>
        </OpsDialog>
      )}
    </Page>
  );
};

const Operations = () => (
  <OpsToastProvider>
    <OperationsPage />
  </OpsToastProvider>
);

export default Operations;
